import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { taxCodeEndpoints } from '@/lib/api/endpoints';
import type { TaxPolicyInput } from '@/types/tax';

// ─── Wire shapes (mirror `tax-simulate.service.ts` in operations) ────────────

export type SimulateLineCategory = 'materials' | 'labor' | 'freight' | 'other';

export interface SimulateLineInput {
  id?: string;
  category: SimulateLineCategory;
  description: string;
  amount: number;
  quantity: number;
}

export interface SimulateRequest {
  zipCode: string;
  lineItems: SimulateLineInput[];
  /**
   * Tax Policy context. The backend has always accepted these; the
   * simulator page now surfaces them so an operator can see which rule
   * a given unit/customer combination lands on.
   *
   * `segment` is the CONTACT's segment. The business unit's own segment
   * wins when it declares one — a unit set to ANY defers to this.
   */
  businessUnitId?: string | null;
  segment?: 'RESIDENTIAL' | 'COMMERCIAL' | null;
  jobTypeId?: string | null;
  /** Simulate as this customer — exemption, sticky tax pin, contact type. */
  contactId?: string | null;
  /**
   * The address state. Documents always carry one; without it a ZIP that
   * is in no tax zone leaves state-scoped rules unmatchable here while a
   * real save would match them.
   */
  stateCode?: string | null;
  /**
   * An unsaved rule to price as if saved, in place of its saved version
   * (`replacesId`). The server runs it through the same matcher as stored rules.
   */
  draftPolicy?: TaxPolicyInput & {
    replacesId?: string;
    /** Apply the draft without matching it — "what would this rule do here?". */
    force?: boolean;
  };
}

export interface SimulateCodeDetail {
  taxCodeId: string;
  /**
   * Long-form `tax_codes.name` ("US State Sales Tax", "GST (Federal)").
   * v60 fetches populate this from `jurDescription`; Canada uses the
   * pre-formatted strings emitted by `canadian-zone-builders.ts`.
   */
  name: string;
  jurisdiction: string | null;
  jurisdictionType: string | null;
  rate: number;
  /**
   * Multi-dim applies-to SET (Brian's backlog #41). Single string still
   * accepted for back-compat with snapshots that pre-date the cutover.
   */
  appliesTo: string | string[];
  /** Human-readable label for the set ("Materials, Labor"). */
  appliesToLabel?: string;
  applied: boolean;
  amount: number;
  reason?: string;
  isAppliesToOverridden: boolean;
  /** A rule override kept off customer documents; shown here regardless. */
  hideOnDocument?: boolean;
}

export interface SimulateLineResult {
  index: number;
  id?: string;
  description: string;
  category: SimulateLineCategory;
  taxLineType: 'materials' | 'labor' | 'freight' | 'flat_rate' | 'exempt';
  subtotal: number;
  codes: SimulateCodeDetail[];
  tax: number;
  total: number;
}

export interface SimulateTotals {
  subtotal: number;
  tax: number;
  total: number;
}

export interface SimulateResponse {
  zipCode: string;
  taxGroup: { id: string; name: string; combinedRate: number } | null;
  resolvedFrom: string;
  /** The Tax Policy rule that shaped this result, when one matched. */
  policy?: {
    id: string;
    name: string;
    taxableBases: string[];
    forcedTaxGroupId?: string | null;
  } | null;
  lines: SimulateLineResult[];
  totals: SimulateTotals;
}

/**
 * POST /tax-codes/simulate — no-persist tax preview. The simulator
 * page calls this every time the user hits the Simulate button.
 *
 * Backend reuses live `TaxResolutionService` + `TaxCalculationService`
 * so the math is guaranteed identical to what an invoice would produce.
 * Failures surface via the standard `useApiMutation` toast.
 */
export function useSimulateTax() {
  return useApiMutation<SimulateResponse, SimulateRequest>({
    mutationFn: (req) =>
      apiPost<SimulateResponse>(operationsClient, taxCodeEndpoints.simulate(), req),
    // Don't toast on every failed simulate — the page itself shows the
    // error inline next to the Simulate button.
    showErrorToast: false,
  });
}
